import React from 'react';
import FAQs from './Faqs';

export default function About() {
  return (
    <div className="min-h-screen bg-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <h1 className="text-4xl font-bold text-gray-100 mb-8 text-center">
          About Us
        </h1>

        {/* Who Are We Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-100 mb-4">
            Who Are We?
          </h2>
          <p className="text-gray-300 leading-relaxed">
            Since you’re on our About Us page, allow us to introduce ourselves. 
            Hello! We’re the friendly team behind <span className="font-medium">traderockfx</span>. 
            Rather than listing every techie, designer, and analyst, we’ll let our founder take the stage. 
            Here’s an introductory word from him:
          </p>
        </section>

        {/* Founder's Story */}
        <section className="mb-12 bg-gray-700 p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">
            Our Origin Story
          </h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            The idea for traderockfx was born in 2017, during a time when the cryptocurrency market 
            was wildly volatile. High volatility sparked my interest in arbitrage deals—super-fast 
            transactions that could capitalize on price differences. However, transferring funds 
            between exchanges was slow. That’s when I recalled the “Triangular Arbitrage” strategy, 
            which allows rapid trades within a single exchange via API.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4">
            I called a school friend—a skilled developer—and asked him to whip up a test app for a 
            leading exchange. Three hours later, I had a rough trading bot. It wasn’t perfect—orders 
            lagged, and there were glitches—but it was a start. We soon brought in analysts to train 
            the bot for extreme scenarios like pumps and dumps, where triple arbitrage shines. 
            Within months, we had a near-complete bot, and the profits started rolling in.
          </p>
        </section>

        {/* Why the Delay Section */}
        <section className="mb-12">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">
            Why Did We Launch in 2020?
          </h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            While the idea took root in 2017, market conditions shifted. Volatility dropped, and a 
            bear market set in. In 2018, we refined the bot for bear markets and quick liquidations. 
            By early 2019, it was running smoothly, handling all market movements flawlessly. That’s 
            when we decided to formalize traderockfx as a company.
          </p>
          <p className="text-gray-300 leading-relaxed">
            We hired lawyers and marketing experts, prioritizing user anonymity and security. 
            However, adhering to KYC and AML policies clashed with our decentralized ethos. We 
            believe these regulations often fail to protect users—just look at “exit scams” on 
            major exchanges. After consulting and preparing documents, we registered in Hong Kong 
            in October 2019. The traderockfx platform officially launched in April 2020 after months 
            of development.
          </p>
        </section>

        {/* KYC/AML Explanation */}
        <section className="mb-12 bg-gray-700 p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">
            How Do We Operate Without KYC/AML?
          </h3>
          <p className="text-gray-300 leading-relaxed">
            We partner with top analytical firms to monitor dark and gray transactions. If suspicious 
            funds enter our system, we freeze the account pending investigation. Meanwhile, regular 
            users enjoy seamless withdrawals—up to 150 BTC daily—without verification. It’s a 
            practical balance of security and freedom at traderockfx.
          </p>
        </section>

        {/* CEO Introduction */}
        <section className="mb-12">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">
            Meet Our CEO
          </h3>
          <p className="text-gray-300 leading-relaxed italic">
            “Oops, almost forgot—my name is Anton, and I’m the CEO of traderockfx. 
            Thanks for sticking with our story!” 
          </p>
        </section>

        {/* Platform Offerings */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-100 mb-4">
            What Does traderockfx Offer?
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            traderockfx is a one-of-a-kind platform for cryptocurrency arbitrage trading. 
            Whether you’re a novice or a pro, our tools make profitable trades simple—no need to juggle 
            multiple exchanges or massive capital. Sign up, and you’ll access popular trading platforms 
            with guaranteed returns.
          </p>
          <p className="text-gray-300 leading-relaxed">
            Security is our priority. We use cold and hot wallets to safeguard funds and cutting-edge 
            encryption to protect your data at traderockfx.
          </p>
        </section>

        {/* Automated Trading */}
        <section className="mb-12 bg-gray-700 p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">
            Automated Trading
          </h3>
          <p className="text-gray-300 leading-relaxed">
            For a one-time fee of 0.005 BTC, you unlock lifelong automated trading with traderockfx. 
            Just click, fill out a form, and our bot handles the rest—delivering daily reports and 
            60% to 64.29% returns on your transaction amount.
          </p>
        </section>

        {/* Manual Trading */}
        <section className="mb-12 bg-gray-700 p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">
            Manual Trading
          </h3>
          <p className="text-gray-300 leading-relaxed">
            No fees here. Choose from three triangular arbitrage options per coin, each with unique 
            profitability and execution times based on market conditions. It’s flexible and tailored 
            to your preferences on traderockfx.
          </p>
        </section>

        {/* Help Section */}
        <section className="text-center">
          <h2 className="text-2xl font-semibold text-gray-100 mb-4">
            Need More Info?
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            Start with our <a href="#" className="text-blue-400 hover:underline">Beginner’s Guide</a>. 
            Still curious? Check the <a href="#" className="text-blue-400 hover:underline">FAQ</a>. 
            If you’re stuck, our <a href="#" className="text-blue-400 hover:underline">Support team</a> 
            at traderockfx is here to help.
          </p>
        </section>
      </div>
      <FAQs />
    </div>
  );
}